import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useRef,
    useState,
} from 'react';
import './tooltip.scss';
import { PreferenceOption } from '@models/communication-center';
import { KEYBOARD_KEYS } from '@constants';
import CloseButtonIcon from '@views/order-details-view/components/cost/tax-tooltip/icons/close-button-icon';

const cancelCloseIcon = './icons/cancel-close.svg';
interface TooltipProps {
    content: NonNullable<React.ReactNode>;
    children: NonNullable<React.ReactNode>;
    direction?: 'top' | 'bottom' | 'left' | 'right'; // Tooltip will be positioned on top by default
    delay?: number; // By default tooltip will be display immediately.
    tooltipStyles?: any; // custom tooltip styles
    onDisplayTooltip?: any;
    tooltipId?: string;
    showTooltip: boolean;
    toggleTooltip?: Dispatch<SetStateAction<boolean>>;
    toggleReimagineTooltip?: (value: boolean) => void;
    isLincoln: boolean;
    isFord: boolean;
    tooltipPreferenceOption?: PreferenceOption;
    onIconClick?: (e?) => void;
    isMobile?: boolean;
    tooltipAriaLabel?: string;
    isFromCommunication?: boolean;
    isTaxTooltip?: boolean;
}
const Tooltip = (props: TooltipProps) => {
    const {
        isFromCommunication,
        isMobile,
        tooltipPreferenceOption,
        toggleReimagineTooltip,
    }: TooltipProps = props;
    const tooltipContentRef = useRef(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const tooltipId: string = tooltipPreferenceOption
        ? 'communication-center-tooltip'
        : 'add-vehicle-tooltip';
    const tooltipCustomStyles =
        props.isLincoln && props.tooltipStyles
            ? { ...props.tooltipStyles }
            : {};
    const [closeTooltip, setCloseTooltip] = useState(true);
    const newTooltip = isFromCommunication || props.isTaxTooltip;

    const generateTooltipAriaLabel = (): string => {
        let ariaLabel: string;

        if (props.tooltipAriaLabel || tooltipPreferenceOption) {
            ariaLabel = props.tooltipAriaLabel;
        } else if (props.isTaxTooltip) {
            ariaLabel = 'View tax section disclaimer';
        } else {
            ariaLabel = 'Where do I find my VIN?';
        }

        return ariaLabel;
    };

    const onClick = () => {
        if (closeTooltip) {
            setCloseTooltip(false);
        } else {
            setCloseTooltip(true);
        }

        if (props.showTooltip !== false) {
            if (newTooltip) {
                toggleReimagineTooltip(false);
            } else {
                props.toggleTooltip(false);
            }

            setCloseTooltip(true);
        } else if (newTooltip) {
            toggleReimagineTooltip(true);
        } else {
            props.toggleTooltip(true);
        }
    };

    const handleIconClick = (e?): void => {
        if (newTooltip) {
            props.onIconClick(e);
        }
    };

    const onTabBackwards = (e): void => {
        if (e.key === KEYBOARD_KEYS.TAB && e.shiftKey) {
            toggleReimagineTooltip(false);
        }
    };

    const closeOnEscapeKey = (e): void => {
        if (e.key === KEYBOARD_KEYS.ESC) {
            if (newTooltip) {
                toggleReimagineTooltip(false);
            } else {
                props.toggleTooltip(false);
            }
        }
    };

    const handleClickOutsideTooltip = (e) => {
        const isOutsideTooltip =
            tooltipContentRef.current &&
            !tooltipContentRef.current.contains(e.target);
        const isMobileAndTooltipOpen =
            isMobile && newTooltip && props.showTooltip;

        if (isMobileAndTooltipOpen) {
            return;
        }

        if (isOutsideTooltip) {
            if (newTooltip) {
                toggleReimagineTooltip(false);
            } else {
                props.toggleTooltip(false);
            }
        }
    };

    useEffect(() => {
        if (props.showTooltip) {
            document.addEventListener(
                'click',
                handleClickOutsideTooltip,
                false
            );
            document.addEventListener('keydown', closeOnEscapeKey, false);
        }

        return () => {
            document.removeEventListener(
                'click',
                handleClickOutsideTooltip,
                false
            );
            document.removeEventListener('keydown', closeOnEscapeKey, false);
        };
    }, [props.showTooltip]);

    useEffect(() => {
        if (
            props.showTooltip &&
            newTooltip &&
            isMobile &&
            closeButtonRef?.current
        ) {
            setTimeout(() => {
                closeButtonRef?.current.focus();
            }, 250);
        }
    }, [props.showTooltip, isMobile]);

    return (
        <div className="tooltip-wrapper" ref={tooltipContentRef}>
            <button
                aria-label={generateTooltipAriaLabel()}
                className="tooltip-children-container"
                onClick={(e) => handleIconClick(e)}
                onKeyDown={onTabBackwards}
                data-testid="tax-tooltip-button"
            >
                {props.children}
            </button>

            {props.showTooltip && (
                <div
                    id={props.tooltipId || tooltipId}
                    data-testid={props.tooltipId || tooltipId}
                    role="dialog"
                    aria-label={generateTooltipAriaLabel()}
                    className={`tooltip-tip ${props.direction || 'top'}`}
                    style={{ ...tooltipCustomStyles }}
                >
                    {props.isFord || newTooltip ? (
                        <button
                            ref={closeButtonRef}
                            data-testid="test-close"
                            onClick={(e) => {
                                e?.stopPropagation();
                                onClick();
                            }}
                            aria-label="Close Dialog"
                            className={`fmc-dialog__close ${
                                props.isLincoln
                                    ? 'fds-icon fds-font--ford-icons__clear'
                                    : ''
                            }`}
                            onKeyDown={(e) => {
                                if (e.key === KEYBOARD_KEYS.TAB) {
                                    toggleReimagineTooltip(false);
                                }
                            }}
                        >
                            {props.isFord && !props.isTaxTooltip ? (
                                <img src={cancelCloseIcon} alt="close-icon" />
                            ) : null}
                            {props.isTaxTooltip && (
                                <CloseButtonIcon color="#333333" />
                            )}
                        </button>
                    ) : null}
                    {props.content}
                </div>
            )}
        </div>
    );
};

export default Tooltip;
